import { supabase } from '../lib/supabase';

// Get all communes
export const getAllCommunes = async () => {
  try {
    const { data, error } = await supabase?.from('communes')?.select('*')?.order('name', { ascending: true });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch communes' };
  }
};

// Get commune by ID
export const getCommuneById = async (id) => {
  try {
    const { data, error } = await supabase?.from('communes')?.select('*')?.eq('id', id)?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to fetch commune' };
  }
};

// Get communes by district
export const getCommunesByDistrict = async (district) => {
  try {
    const { data, error } = await supabase?.from('communes')?.select('*')?.eq('district', district)?.order('name', { ascending: true });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch communes by district' };
  }
};

// Get communes by price range
export const getCommunesByPriceRange = async (minPrice, maxPrice) => {
  try {
    const { data, error } = await supabase?.rpc('get_communes_by_price_range', {
        min_price: minPrice,
        max_price: maxPrice
      });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch communes by price range' };
  }
};

// Search communes by amenity
export const searchCommunesByAmenity = async (amenity) => {
  try {
    const { data, error } = await supabase?.rpc('search_communes_by_amenity', {
        search_amenity: amenity
      });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed search communes by amenity' };
  }
};

// Create new commune (admin only)
export const createCommune = async (communeData) => {
  try {
    const { data, error } = await supabase?.from('communes')?.insert([communeData])?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to create commune' };
  }
};

// Update commune (admin only)
export const updateCommune = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('communes')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update commune' };
  }
};

// Delete commune (admin only)
export const deleteCommune = async (id) => {
  try {
    const { error } = await supabase?.from('communes')?.delete()?.eq('id', id);

    if (error) {
      return { error: error?.message };
    }

    return { error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { error: 'Failed to delete commune' };
  }
};

// Get properties in a commune
export const getPropertiesInCommune = async (communeId) => {
  try {
    const { data, error } = await supabase?.from('properties')?.select(`
        *,
        agent:user_profiles!agent_id(id, full_name, email, phone)
      `)?.eq('commune_id', communeId)?.eq('status', 'available')?.order('created_at', { ascending: false });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch properties in commune' };
  }
};

// Get communes with property count - Fixed JSON aggregation
export const getCommunesWithPropertyCount = async () => {
  try {
    const { data, error } = await supabase
      ?.from('communes')
      ?.select(`
        *,
        properties!commune_id(count)
      `)
      ?.order('name', { ascending: true });

    if (error) {
      return { data: [], error: error?.message };
    }

    // Transform the data to flatten the property count safely
    const communesWithCount = (data || [])?.map(commune => ({
      ...commune,
      propertyCount: commune?.properties?.[0]?.count || 0
    }));

    return { data: communesWithCount, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch communes with property count' };
  }
};

// Get commune statistics
export const getCommuneStats = async () => {
  try {
    const { data: communes, error: communeError } = await supabase
      ?.from('communes')
      ?.select('id, district, population');

    if (communeError) {
      return { data: null, error: communeError?.message };
    }

    const totalCommunes = communes?.length || 0;
    const totalPopulation = communes?.reduce((sum, commune) => sum + (commune?.population || 0), 0);
    const uniqueDistricts = [...new Set(communes?.map(c => c?.district)?.filter(Boolean))]?.length || 0;

    const stats = {
      total_communes: totalCommunes,
      total_population: totalPopulation,
      unique_districts: uniqueDistricts,
      average_population: totalCommunes > 0 ? Math.round(totalPopulation / totalCommunes) : 0
    };

    return { data: stats, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to fetch commune statistics' };
  }
};

// Create communeService object with all methods
export const communeService = {
  getAllCommunes,
  getCommuneById,
  getCommunesByDistrict,
  getCommunesByPriceRange,
  searchCommunesByAmenity,
  createCommune,
  updateCommune,
  deleteCommune,
  getPropertiesInCommune,
  getCommunesWithPropertyCount,
  getCommuneStats
};

export default {
  getAllCommunes,
  getCommuneById,
  getCommunesByDistrict,
  getCommunesByPriceRange,
  searchCommunesByAmenity,
  createCommune,
  updateCommune,
  deleteCommune,
  getPropertiesInCommune,
  getCommunesWithPropertyCount,
  getCommuneStats
};